import React, { useState, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Plus,
  Gauge,
  CheckCircle2,
  AlertCircle,
  Clock,
  MapPin,
  Eye,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@shared/ui/dialog";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useSubMeterSummary,
  useSubMeterList,
  useSearchSubmeters,
  useAddSubMeter,
} from "@features/mx/device-management/hooks";
import { MODAL_PAGE_SIZE, PAGE_SIZE } from "@shared/utils/constants";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface SubMeterTabProps {
  meterId: number;
  meterDetail: any;
}

interface SubMeterData {
  id: string;
  meterNo: string;
  deviceNo: string;
  premise: string;
  assignmentStatus: string;
  readStatus: string;
  lastRead: string;
  lastReadDate: string;
}

interface DialogSubMeterData {
  id: string;
  meterNo: string;
  deviceNo: string;
  premise: string;
  linkedTo: string;
  assignmentStatus: string;
}

export const SubMeterTab: React.FC<SubMeterTabProps> = ({
  meterId,
  meterDetail,
}) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalPage, setModalPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // KPI summary (count cards only)
  const { data: subMeterSummaryResponse } = useSubMeterSummary({
    meterId,
    remoteUtilityId,
  });

  // Paginated list for the main table
  const { data: subMeterListResponse, isLoading } = useSubMeterList({
    meterId,
    remoteUtilityId,
    page,
    limit: PAGE_SIZE,
    search: searchTerm,
  });

  const { data: subMeterSearchResponse } = useSearchSubmeters(
    remoteUtilityId,
    modalSearchTerm,
    modalPage,
    MODAL_PAGE_SIZE,
  );

  console.log("subMeterSearchResponse", subMeterSearchResponse);
  const addSubMeterMutation = useAddSubMeter();

  // Extract KPI counts dynamically from API summary or fallback
  const summaryData = subMeterSummaryResponse || {};
  const kpis = useMemo(
    () => [
      {
        title: "Total Sub-meters",
        value: summaryData.totalSubMeters,
        icon: <Gauge className="h-8 w-8 text-blue-500" />,
        tooltip: "Total number of sub-meters linked to this master-meter.",
      },
      {
        title: "Assigned Sub-meters",
        value: summaryData.assignedSubMeters,
        icon: <CheckCircle2 className="h-8 w-8 text-green-500" />,
        tooltip: "Sub-meters that are assigned to premises.",
      },
      {
        title: "Unassigned Sub-meters",
        value: summaryData.unassignedSubMeters,
        icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
        tooltip: "Sub-meters that are currently unassigned.",
      },
      {
        title: "Installed Sub-meters",
        value: summaryData.installedSubMeters,
        icon: <Clock className="h-8 w-8 text-purple-500" />,
        tooltip: "Sub-meters that have been installed and are active.",
      },
    ],
    [summaryData],
  );

  // Map list response items to SubMeterData structure
  const subMetersData = useMemo(() => {
    if (!subMeterListResponse?.results) return [];

    return subMeterListResponse.results.map((item: any) => {
      const getMeterNo = (m: any) => {
        if (!m) return "N/A";
        if (typeof m === "object") return m.meterNumber || "N/A";
        return m;
      };
      return {
        id: item.id?.toString(),
        meterNo: getMeterNo(item.meterNumber),
        deviceNo: item.deviceNo || "N/A",
        premise: item.premiseName || "N/A",
        assignmentStatus: item.assignmentStatus || "N/A",
        readStatus: item.readStatus || "N/A",
        lastRead: item.lastRead || "N/A",
        lastReadDate: item.lastReadDate || "N/A",
      };
    });
  }, [subMeterListResponse]);

  const columns: TableColumn<SubMeterData>[] = useMemo(
    () => [
      {
        key: "meterNo",
        header: "Meter No",
      },
      {
        key: "deviceNo",
        header: "Device No",
      },
      {
        key: "premise",
        header: "Premise",
        render: (val) => (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            {val || "N/A"}
          </div>
        ),
      },
      {
        key: "assignmentStatus",
        header: "Assignment",
        type: "badge",
        badgeConfig: {
          getVariant: (val) => {
            if (val === "Assigned" || val === "ASSIGNED") return "success";
            return "secondary";
          },
        },
      },
      {
        key: "readStatus",
        header: "Read Status",
        render: (val) => (
          <div className="flex items-center gap-1.5">
            <div
              className={`h-2 w-2 rounded-full ${
                val === "Success" || val === "SUCCESS"
                  ? "bg-green-500"
                  : "bg-amber-500"
              }`}
            />
            {val || "Pending"}
          </div>
        ),
      },
      {
        key: "lastRead",
        header: "Last Read",
      },
      {
        key: "lastReadDate",
        header: "Date",
        render: (val) => (
          <span className="text-muted-foreground text-xs">{val || "N/A"}</span>
        ),
      },
    ],
    [],
  );

  const dialogColumns: TableColumn<DialogSubMeterData>[] = useMemo(
    () => [
      {
        key: "meterNumber",
        header: "Meter No",
        render: (val: any) => {
          if (val && typeof val === "object") {
            return val.meterNumber || "N/A";
          }
          return val || "N/A";
        },
      },
      {
        key: "deviceNo",
        header: "Device No",
      },
      {
        key: "premiseName",
        header: "Premise",
      },
      {
        key: "linkedTo",
        header: "Linked To",
        render: (val: any) => {
          if (!val) return "N/A";
          if (Array.isArray(val)) {
            return (
              val
                .map((item) =>
                  typeof item === "object" ? item?.meterNumber : item,
                )
                .filter(Boolean)
                .join(", ") || "N/A"
            );
          }
          if (typeof val === "object") {
            return val.meterNumber || "N/A";
          }
          return val || "N/A";
        },
      },
      {
        key: "assignmentStatus",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: (val) => {
            if (val === "Assigned" || val === "ASSIGNED") return "secondary";
            return "outline";
          },
        },
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        label: "View Detail",
        icon: <Eye className="h-4 w-4 text-muted-foreground" />,
        onClick: (row: SubMeterData) => {
          navigate(`/mx/device-management/meter/view/${row.id}`);
        },
      },
    ],
    [navigate],
  );

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleModalSearch = (term: string) => {
    setModalSearchTerm(term);
    setModalPage(1);
  };

  const handleSubmit = () => {
    if (selectedRows.length === 0) return;
    addSubMeterMutation.mutate(
      {
        master_meter_id: meterId,
        sub_meter_ids: selectedRows.map(Number),
        remote_utility_id: Number(remoteUtilityId),
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedRows([]);
        },
      },
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalItems = subMeterListResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  const totalModalItems = subMeterSearchResponse?.count || 0;
  const totalModalPages = Math.ceil(totalModalItems / MODAL_PAGE_SIZE) || 1;

  const modalPagination = useMemo(
    () => ({
      currentPage: modalPage,
      totalPages: totalModalPages,
      totalItems: totalModalItems,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: (modalPage - 1) * MODAL_PAGE_SIZE + 1,
      endItem: Math.min(modalPage * MODAL_PAGE_SIZE, totalModalItems),
    }),
    [modalPage, totalModalPages, totalModalItems],
  );

  return (
    <div className="space-y-6">
      {/* Top Header Section with Add Sub-meter Button */}
      <div className="flex items-center justify-between pb-2">
        <div>
          <h2 className="text-lg font-semibold text-neutral-800">
            Sub-meter Management
          </h2>
          <p className="text-xs text-muted-foreground">
            Monitor and configure linked sub-meters for this master-meter
          </p>
        </div>
        <Dialog
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) {
              setSelectedRows([]);
              setModalSearchTerm("");
              setModalPage(1);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Sub-meter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-6 overflow-hidden">
            <DialogHeader>
              <DialogTitle>Link New Sub-meters</DialogTitle>
            </DialogHeader>
            <div className="py-4 flex-1 overflow-y-auto min-h-0 px-2">
              <GenericTable
                data={subMeterSearchResponse?.results || []}
                columns={dialogColumns}
                rowKey="id"
                onSearch={handleModalSearch}
                onSelectionChange={setSelectedRows}
                selectable={true}
                selectedRows={selectedRows}
                searchPlaceholder="Search all sub-meters by number, device or premise..."
                pagination={modalPagination}
                onPageChange={(newPage) => setModalPage(newPage)}
              />
            </div>
            <DialogFooter className="flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedRows([]);
                  setModalSearchTerm("");
                  setModalPage(1);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={
                  selectedRows.length === 0 || addSubMeterMutation.isPending
                }
              >
                {addSubMeterMutation.isPending ? "Linking..." : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.value}
            icon={card.icon}
            tooltip={card.tooltip}
          />
        ))}
      </div>

      {/* Main List Section */}
      <GenericTable
        data={subMetersData}
        columns={columns}
        rowKey="id"
        onSearch={handleSearch}
        searchPlaceholder="Search by meter or device number..."
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="icons"
      />
    </div>
  );
};
